import {
  Component, ViewEncapsulation,
  ChangeDetectionStrategy, ViewChild, TemplateRef, OnInit } from '@angular/core';

import { AfterViewInit, ChangeDetectorRef, Injectable } from '@angular/core';
import { ActivatedRoute, Router} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ExceptionHelper } from './../../../helper';

import {
  startOfDay, endOfDay,
  subDays, addDays,
  startOfMonth, endOfMonth,
  startOfWeek, endOfWeek,
  isSameDay, isSameMonth,
  addWeeks, subWeeks,
  addMonths, subMonths,
  addHours, setHours,
  setMinutes,
  format
} from 'date-fns';

import { Observable } from 'rxjs';
import { Subject } from 'rxjs';

import { debounceTime, map } from 'rxjs/operators';

import { CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent,
CalendarDateFormatter, CalendarEventTitleFormatter,
CalendarMonthViewDay } from 'angular-calendar'; // import should be from `angular-calendar` in your app
import * as moment from 'moment';
import { CustomDateFormatter } from './custom-date-formatter.provider';
import { CustomEventTitleFormatter } from './custom-event-title-formatter.provider';

import { ScheduleService } from './schedule.service';

const colors: any = {
  red: {
    primary: '#f86c6b',
    secondary: '#FAE3E3'
  },
  blue: {
    primary: '#63c2de',
    secondary: '#D1E8FF'
  },
  green: {
    primary: '#209744',
    secondary: '#CDFFED'
  },
  purple: {
    primary: '#8173E5',
    secondary: '#E9E8FF',
  },
  yellow: {
    primary: '#ffc107',
    secondary: '#FDF1BA'
  },
  pink: {
    primary: '#CC64E2',
    secondary: '#F9E5FF'
  },
  black: {
    primary: '#000000',
    secondary: '#000000'
  }
};

interface Schedule {
  beginDate: string;
  confirmed: number;
  customerId: number;
  customerName: string;
  customerShortName: string;
  finishDate: string;
  id: number;
  type: number;
  teamColor: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'schedule.component.html',
  styleUrls: ['../../../../scss/vendors/angular-calendar/angular-calendar.scss'],
  styles: [
    `
    .cell-totals {
      margin: 5px;
      text-align: center;
    }
    .badge {
      margin-right: 5px;
    }

    .fill-height {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }
  `
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter
    },
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter
    }
   ]
})

export class ScheduleComponent implements OnInit {
  view: string = 'month';

  viewDate: Date = new Date();

  // modalData: {
  //   action: string;
  //   event: CalendarEvent;
  // };

  // actions: CalendarEventAction[] = [{
  //   label: '<i class="fa fa-fw fa-pencil"></i>',
  //   onClick: ({event}: {event: CalendarEvent}): void => {
  //   }
  // }, {
  //   label: '<i class="fa fa-fw fa-times"></i>',
  //   onClick: ({event}: {event: CalendarEvent}): void => {
  //     this.events = this.events.filter(iEvent => iEvent !== event);
  //   }
  // }];

  // refresh: Subject<any> = new Subject();

  events$: Observable<Array<CalendarEvent<{ schedule: Schedule }>>>;

  activeDayIsOpen: boolean = false;

  constructor(
    private exception: ExceptionHelper,
    private router: Router,
    private translate: TranslateService,
    private scheduleService: ScheduleService,
    private chRef: ChangeDetectorRef
  ) {
    translate.setDefaultLang('en-us');
  }

  increment(): void {

    const addFn: any = {
      day: addDays,
      week: addWeeks,
      month: addMonths
    }[this.view];

    this.viewDate = addFn(this.viewDate);

  }

  decrement(): void {

    const subFn: any = {
      day: subDays,
      week: subWeeks,
      month: subMonths
    }[this.view];

    this.viewDate = subFn(this.viewDate);

  }

  today(): void {
    this.viewDate = new Date();
  }

  dayClicked({date, events}: {date: Date, events: CalendarEvent[]}): void {

    if (isSameMonth(date, this.viewDate)) {
      if (
        (isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
        this.viewDate = date;
      }
    }
  }

  // eventTimesChanged({
  //   event,
  //   newStart,
  //   newEnd
  // }: CalendarEventTimesChangedEvent): void {
  //   event.start = newStart;
  //   event.end = newEnd;
  //   this.handleEvent('Dropped or resized', event);
  //   this.refresh.next();
  // }

  // handleEvent(action: string, event: CalendarEvent): void {
  //   this.modalData = { event, action };
  //   // this.modal.open(this.modalContent, { size: 'lg' });
  // }

  beforeMonthViewRender({ body }: { body: CalendarMonthViewDay[] }): void {
    body.forEach(cell => {
      const groups: any = {};
      cell.events.forEach((event: CalendarEvent<{ type: string }>) => {
        groups[event.meta.type] = groups[event.meta.type] || [];
        groups[event.meta.type].push(event);
      });
      cell['eventGroups'] = Object.entries(groups);
    });
  }

  eventClicked({ event }: { event: CalendarEvent }): void {

    const type = event.meta.data.type;
    const id = event.meta.data.id;

    if (type === 0 || type === 1) {
      this.router.navigate(['/home/preventive-maintenance/detail/' + id]);
    }
    else if (type === 2) {
      this.router.navigate(['/home/service/detail/' + id]);
    }
  }

  ngOnInit(): void {
    this.fetchEvents();
  }

  fetchEvents(): void {
    const getStart: any = {
      month: startOfMonth,
      week: startOfWeek,
      day: startOfDay
    }[this.view];

    const getEnd: any = {
      month: endOfMonth,
      week: endOfWeek,
      day: endOfDay
    }[this.view];

    this.activeDayIsOpen = false;

    this.events$ = this.scheduleService.getSchedule(format(this.viewDate, 'YYYY-MM-DD'))
      .pipe(
        map(
          ({ workScheduleListModel }: { workScheduleListModel: Schedule[] }) => {
            return workScheduleListModel.map((schedule: Schedule) => {
              return {
                start: moment(schedule.beginDate, 'YYYY-MM-DD HH:mm:ss').toDate(),
                end: (schedule.finishDate == null ?
                  null : moment(schedule.finishDate, 'YYYY-MM-DD HH:mm:ss').toDate()),
                title: this.switchType(schedule.type) + schedule.id + ' (' + schedule.customerShortName + ')',
                color: this.switchColor(schedule.teamColor),
                meta: {
                  data: schedule
                }
              };
            });
        },
        error => {
          this.exception.showError(error, 'showDanModalBtn');
        })
      );
  }

  switchType(int): any {
    switch (int) {
      case 0:
        return '(T&C) PM-';
      case 1:
        return 'PM-';
      case 2:
        return 'SC-';
      default:
        return '';
    }
  }

  switchColor(int): any {
    switch (int) {
      case 0:
        return colors.red;
      case 1:
        return colors.purple;
      case 2:
        return colors.yellow;
      case 3:
        return colors.pink;
      default:
        return colors.green;
    }
  }

}



// WEBPACK FOOTER //
// /Users/adminuser/Documents/pentagon/pentagon-admin/src/app/views/home/schedule/schedule.component.ts